import { Link } from "@tanstack/react-router";
import { Handle, type NodeProps, Position } from "@xyflow/react";
import { X } from "lucide-react";
import { PluckThumbnail } from "@/components/pluck-thumbnail";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";
import { PLUCK_NODE_WIDTH, type PluckNode } from "@/lib/board-graph";
import { cn } from "@/lib/utils";
import { useBoardData } from "./board-context";

export function PluckNodeView({ id, data, selected }: NodeProps<PluckNode>) {
	const { plucksById, onRemoveNode } = useBoardData();
	const pluck = plucksById.get(data.pluckId);

	return (
		<div
			className={cn(
				"group relative flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm",
				selected && "ring-2 ring-ring"
			)}
			style={{ width: PLUCK_NODE_WIDTH }}
		>
			<div className="flex aspect-[4/3] items-center justify-center bg-[repeating-conic-gradient(var(--color-muted)_0%_25%,transparent_0%_50%)] bg-size-[14px_14px] p-2">
				{pluck ? (
					<PluckThumbnail
						alt={pluck.sourceTitle ?? pluck.sourceDomain}
						assetId={pluck.screenshotAssetId}
						className="max-h-full max-w-full rounded-sm shadow-xs"
					/>
				) : (
					<p className="text-muted-foreground text-xs">Pluck unavailable</p>
				)}
			</div>
			<div className="flex items-center gap-2 border-t px-3 py-2">
				<div className="min-w-0 flex-1">
					<p className="truncate font-medium text-xs">
						{pluck?.sourceDomain ?? "Deleted pluck"}
					</p>
					{pluck && (
						<Link
							className="truncate text-[11px] text-muted-foreground hover:underline"
							onClick={() => track("pluck.opened", { from: "board" })}
							params={{ pluckId: pluck.id }}
							to="/plucks/$pluckId"
						>
							&lt;{pluck.rootTag}&gt; · open
						</Link>
					)}
				</div>
				<Button
					aria-label="Remove from board"
					className="opacity-0 group-hover:opacity-100"
					onClick={() => onRemoveNode(id)}
					size="icon-sm"
					variant="ghost"
				>
					<X />
				</Button>
			</div>
			<Handle position={Position.Right} type="source" />
		</div>
	);
}
